import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  ArrowLeft,
  CheckCircle2,
  Users,
  ClipboardCheck,
  DollarSign,
  MessageSquare,
  Video,
  Trophy,
  ChevronRight,
  Mail,
  Phone,
  MapPin,
  Instagram,
  Twitter,
  Facebook,
} from "lucide-react";
import { useSiteContentCache, SiteLoadingScreen } from "../contexts/SiteContentCache";
import TranslateWidget from "../components/TranslateWidget";

const API = process.env.REACT_APP_BACKEND_URL;

const BENEFIT_ICONS = [Users, ClipboardCheck, DollarSign, MessageSquare, Video, Trophy];

const KNOWN_LEAD_FIELDS = ["name", "email", "phone", "player_age", "parent_name", "soccer_experience", "organization", "num_players", "message"];

const DEFAULT_COACH_FIELDS = [
  { id: "name", label: "Coach / Contact Name", type: "text", placeholder: "Your full name", required: true, enabled: true, maps_to: "name", options: [] },
  { id: "organization", label: "Current Club / Organization", type: "text", placeholder: "Current club or organization name", required: false, enabled: true, maps_to: "organization", options: [] },
  { id: "email", label: "Email Address", type: "email", placeholder: "coach@email.com", required: true, enabled: true, maps_to: "email", options: [] },
  { id: "phone", label: "Phone Number", type: "tel", placeholder: "(555) 555-5555", required: true, enabled: true, maps_to: "phone", options: [] },
  { id: "player_age", label: "Team Age Group(s)", type: "select", placeholder: "Select age group", required: true, enabled: true, maps_to: "player_age", options: ["U8-U9", "U10-U11", "U12-U13", "U14-U15", "U16-U17", "U18-U19", "Girls High School", "Multiple Age Groups"] },
  { id: "num_players", label: "Number of Players", type: "text", placeholder: "e.g., 16", required: false, enabled: true, maps_to: "num_players", options: [] },
  { id: "message", label: "Tell Us About Your Team", type: "textarea", placeholder: "What are you looking for in a club? Current competitive level, goals for the team, any questions...", required: false, enabled: true, maps_to: "message", options: [] },
];

const CoachesPage = () => {
  const { data: cachedContent, loading: cacheLoading } = useSiteContentCache();

  if (!cachedContent && cacheLoading) return <SiteLoadingScreen />;

  const content = cachedContent?.coaches_page || {};
  const logoUrl = cachedContent?.logo_url || "";
  const footerData = cachedContent?.footer || {};

  return (
    <div className="min-h-screen" data-testid="coaches-page">
      <Helmet>
        <title>Bring Your Team to Commonwealth FC | Coach Recruitment Charlottesville</title>
        <meta name="description" content="Coaches — bring your existing team to Commonwealth FC. Keep your roster, get club admin handled, low costs, TeamSnap included, and recorded games. Charlottesville, VA." />
        <meta property="og:title" content="Bring Your Team | Commonwealth FC" />
        <meta property="og:description" content="Coaches — bring your team. Keep your roster, get admin handled, low costs." />
        <meta property="og:url" content="https://commonwealthfc.org/coaches" />
        <link rel="canonical" href="https://commonwealthfc.org/coaches" />
      </Helmet>

      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 bg-[#0C2E3C]/95 backdrop-blur-sm border-b border-[#F0F4F3]/10" data-testid="coaches-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img src={logoUrl} alt="Commonwealth FC" className="h-9 w-auto" />
            <span className="text-sm font-bold uppercase tracking-wider text-[#F0F4F3] hidden sm:block">
              Commonwealth FC
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-[#F0F4F3]/90 hover:text-[#C8E64E] font-semibold text-sm uppercase tracking-wider transition-colors" data-testid="back-link">
              <ArrowLeft size={16} /> Back
            </Link>
            <TranslateWidget />
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-[#0C2E3C]" data-testid="coaches-hero">
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 20.5V18H0v-2h20v-2H0v-2h20v-2H0V8h20V6H0V4h20V2H0V0h22v20h2V0h2v20h2V0h2v20h2V0h2v20h2V0h2v22H20v-1.5zM0 20h2v20H0V20zm4 0h2v20H4V20zm4 0h2v20H8V20zm4 0h2v20h-2V20zm4 0h2v20h-2V20zm4 4h20v2H20v-2zm0 4h20v2H20v-2zm0 4h20v2H20v-2zm0 4h20v2H20v-2z' fill='%23ffffff' fill-opacity='.15'/%3E%3C/svg%3E")`
        }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-[#C8E64E] font-semibold text-sm uppercase tracking-widest mb-4">
              For Coaches & Team Leaders
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F0F4F3] mb-4">
              {content.hero_title || "BRING YOUR TEAM HOME"}
            </h1>
            <p className="text-[#F0F4F3]/90 text-xl sm:text-2xl font-light mb-6">
              {content.hero_subtitle || "You built the team. We handle the rest."}
            </p>
            <p className="text-[#F0F4F3]/70 text-lg mb-8 max-w-2xl leading-relaxed">
              {content.hero_description || "Commonwealth FC gives coaches the freedom to focus on what matters — developing players and winning games. We take care of everything else."}
            </p>
            <Button
              onClick={() => document.getElementById("coach-form")?.scrollIntoView({ behavior: "smooth" })}
              className="bg-[#C8E64E] hover:bg-[#B5D33E] text-[#0C2E3C] font-bold text-sm uppercase tracking-wider px-8 h-14 rounded-none"
              data-testid="hero-cta-btn"
            >
              {content.hero_button || "Start a Conversation"} <ChevronRight size={18} className="ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 bg-[#F0F4F3]" data-testid="coaches-benefits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0C2E3C] mb-4 text-center">
            WHY COACHES CHOOSE US
          </h2>
          <p className="text-center text-[#6B8A8F] text-lg mb-16 max-w-2xl mx-auto">
            Everything you need to run a successful program — without the headaches
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {(content.benefits || []).map((benefit, index) => {
              const Icon = BENEFIT_ICONS[index] || Users;
              return (
                <div
                  key={`benefit-${index}-${benefit.title}`}
                  className="bg-white p-8 border-l-4 border-[#C8E64E] hover:shadow-lg transition-shadow"
                  data-testid={`benefit-card-${index}`}
                >
                  <Icon className="w-10 h-10 text-[#0C2E3C] mb-4" strokeWidth={1.5} />
                  <h3 className="font-bold text-base uppercase tracking-wider text-[#0C2E3C] mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-[#6B8A8F] text-base leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Coach Inquiry Form */}
      <CoachForm content={content} formFields={content.form_fields?.length > 0 ? content.form_fields : DEFAULT_COACH_FIELDS} consentText={cachedContent?.coaches_page?.consent_text} />

      {/* CTA */}
      <section className="py-20 bg-[#0C2E3C]" data-testid="coaches-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#F0F4F3] mb-4">
            {content.cta_heading || "Ready to Make the Move?"}
          </h2>
          <p className="text-[#F0F4F3]/70 text-lg mb-8 max-w-2xl mx-auto">
            {content.cta_description || "Join a club that values coaches and invests in your success."}
          </p>
          <Button
            onClick={() => document.getElementById("coach-form")?.scrollIntoView({ behavior: "smooth" })}
            className="bg-[#C8E64E] hover:bg-[#B5D33E] text-[#0C2E3C] font-bold text-sm uppercase tracking-wider px-8 h-14 rounded-none"
          >
            {content.cta_button || "Get in Touch"}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0C2E3C] py-8 border-t border-[#F0F4F3]/10" data-testid="coaches-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-3">
                <img src={logoUrl} alt="Commonwealth FC" className="h-10 w-auto object-contain" />
                <div>
                  <h3 className="font-bold text-lg text-[#F0F4F3]">Commonwealth FC</h3>
                  <p className="text-[#F0F4F3]/50 text-sm mt-0.5">BUILT DIFFERENT. BUILT TOGETHER.</p>
                </div>
              </div>
              <div className="flex gap-4 mt-3">
                {footerData.instagram && <a href={footerData.instagram} target="_blank" rel="noopener noreferrer" className="text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors" aria-label="Instagram"><Instagram size={20} /></a>}
                {footerData.twitter && <a href={footerData.twitter} target="_blank" rel="noopener noreferrer" className="text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors" aria-label="Twitter"><Twitter size={20} /></a>}
                {footerData.facebook && <a href={footerData.facebook} target="_blank" rel="noopener noreferrer" className="text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors" aria-label="Facebook"><Facebook size={20} /></a>}
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-sm uppercase tracking-wider text-[#F0F4F3] mb-4">Contact</h4>
              <div className="space-y-3 text-sm">
                {footerData.email && <a href={`mailto:${footerData.email}`} className="flex items-center gap-2 text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors"><Mail size={16} />{footerData.email}</a>}
                {footerData.phone && <a href={`tel:${footerData.phone}`} className="flex items-center gap-2 text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors"><Phone size={16} />{footerData.phone}</a>}
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-sm uppercase tracking-wider text-[#F0F4F3] mb-4">Quick Links</h4>
              <div className="space-y-2 text-sm">
                <Link to="/" className="block text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors">Home</Link>
                <Link to="/join" className="block text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors">Register</Link>
                <Link to="/coaches" className="block text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors">Bring Your Team</Link>
                <Link to="/livestream" className="block text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors">Live Streams</Link>
              </div>
            </div>
          </div>
          <div className="border-t border-[#F0F4F3]/10 mt-6 pt-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-[#F0F4F3]/40 text-sm">
            <span>&copy; {new Date().getFullYear()} Commonwealth FC. All rights reserved.</span>
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="hover:text-[#C8E64E] transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-[#C8E64E] transition-colors">Terms</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

const DEFAULT_COACH_CONSENT = "By submitting this form, you agree to receive text messages from Commonwealth FC regarding registration, scheduling, and program updates. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for assistance.";

const CoachForm = ({ content, formFields, consentText }) => {
  const activeFields = (formFields || DEFAULT_COACH_FIELDS).filter(f => f.enabled);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formLoadedAt] = useState(() => Date.now() / 1000);

  const setField = (id, value) => setFormData(prev => ({ ...prev, [id]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { lead_type: "coach", form_loaded_at: formLoadedAt };
      const custom = {};
      activeFields.forEach(field => {
        const val = formData[field.id] || "";
        if (field.maps_to && KNOWN_LEAD_FIELDS.includes(field.maps_to)) {
          payload[field.maps_to] = val;
        } else if (val) {
          custom[field.label] = val;
        }
      });
      if (Object.keys(custom).length > 0) payload.custom_fields = custom;
      if (formData.website) payload.website = formData.website;
      if (!payload.name) payload.name = payload.email || "Unknown";
      if (!payload.email) payload.email = "noemail@placeholder.com";
      if (!payload.phone) payload.phone = "";
      if (!payload.player_age) payload.player_age = "Not specified";
      await axios.post(`${API}/api/leads/submit`, payload);
      setSubmitted(true);
    } catch (err) {
      // Submit failed
    } finally {
      setLoading(false);
    }
  };

  const isSubmitDisabled = loading || activeFields.some(f => f.required && !formData[f.id]?.trim?.());

  // Split fields into pairs for 2-column grid rendering
  const renderField = (field) => (
    <div key={field.id}>
      <Label htmlFor={`coach-${field.id}`} className="text-[#0C2E3C] font-semibold text-sm">{field.label}</Label>
      {field.type === "select" ? (
        <select
          id={`coach-${field.id}`}
          required={field.required}
          value={formData[field.id] || ""}
          onChange={(e) => setField(field.id, e.target.value)}
          className="mt-1 w-full px-3 py-2 border border-[#E5E0D8] bg-white text-[#0C2E3C] focus:border-[#0C2E3C] focus:outline-none focus:ring-1 focus:ring-[#0C2E3C] text-sm"
          data-testid={`coach-field-${field.id}`}
        >
          <option value="">{field.placeholder || "Select..."}</option>
          {(field.options || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      ) : field.type === "textarea" ? (
        <textarea
          id={`coach-${field.id}`}
          required={field.required}
          value={formData[field.id] || ""}
          onChange={(e) => setField(field.id, e.target.value)}
          className="mt-1 w-full px-3 py-2 border border-[#E5E0D8] bg-white text-[#0C2E3C] focus:border-[#0C2E3C] focus:outline-none focus:ring-1 focus:ring-[#0C2E3C] min-h-[100px] resize-y text-sm"
          placeholder={field.placeholder}
          rows={4}
          data-testid={`coach-field-${field.id}`}
        />
      ) : (
        <Input
          id={`coach-${field.id}`}
          type={field.type}
          required={field.required}
          value={formData[field.id] || ""}
          onChange={(e) => setField(field.id, e.target.value)}
          className="mt-1 border-[#E5E0D8] rounded-none focus:border-[#0C2E3C] focus:ring-[#0C2E3C]"
          placeholder={field.placeholder}
          data-testid={`coach-field-${field.id}`}
        />
      )}
    </div>
  );

  // Group non-textarea fields in pairs for grid, textarea fields span full width
  const fieldRows = [];
  let pair = [];
  activeFields.forEach(field => {
    if (field.type === "textarea") {
      if (pair.length > 0) { fieldRows.push(pair); pair = []; }
      fieldRows.push([field]);
    } else {
      pair.push(field);
      if (pair.length === 2) { fieldRows.push(pair); pair = []; }
    }
  });
  if (pair.length > 0) fieldRows.push(pair);

  return (
    <section id="coach-form" className="py-20 bg-white" data-testid="coach-form-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0C2E3C] mb-4 text-center">
          LET{"'"}S TALK
        </h2>
        <p className="text-center text-[#6B8A8F] text-lg mb-12 max-w-xl mx-auto">
          Tell us about your team and we{"'"}ll reach out to discuss how Commonwealth FC can support your program.
        </p>

        {submitted ? (
          <div className="text-center py-12 bg-[#F0F4F3] border-t-4 border-[#C8E64E] p-8" data-testid="coach-form-success">
            <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h3 className="font-heading text-2xl font-bold text-[#0C2E3C] mb-2">
              We{"'"}ll Be in Touch
            </h3>
            <p className="text-[#6B8A8F] text-base max-w-md mx-auto">
              Thank you for your interest in Commonwealth FC. A member of our leadership team will reach out within 48 hours to schedule a call.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 bg-[#F0F4F3] p-8 border-t-4 border-[#C8E64E]" data-testid="coach-inquiry-form">
            {fieldRows.map((row, idx) => (
              row.length === 1 && row[0].type === "textarea" ? (
                <div key={row[0].id}>{renderField(row[0])}</div>
              ) : (
                <div key={`row-${row.map(f => f.id).join("-")}`} className="grid sm:grid-cols-2 gap-6">
                  {row.map(renderField)}
                </div>
              )
            ))}
            {/* Honeypot */}
            <div style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, overflow: "hidden" }} aria-hidden="true" tabIndex={-1}>
              <input type="text" name="website" value={formData.website || ""} onChange={(e) => setField("website", e.target.value)} autoComplete="off" tabIndex={-1} />
            </div>
            <Button
              type="submit"
              disabled={isSubmitDisabled}
              className="w-full bg-[#0C2E3C] hover:bg-[#0C2E3C]/90 text-[#F0F4F3] font-bold uppercase tracking-wider text-base h-14 rounded-none"
              data-testid="coach-submit-btn"
            >
              {loading ? "SUBMITTING..." : "SUBMIT INQUIRY"}
            </Button>
            {(consentText !== "" && (consentText || DEFAULT_COACH_CONSENT)) && (
              <p className="text-[11px] text-[#6B8A8F] leading-relaxed mt-3" data-testid="coach-consent-text">
                {consentText || DEFAULT_COACH_CONSENT}
              </p>
            )}
          </form>
        )}
      </div>
    </section>
  );
};

export default CoachesPage;
